import type { ReactNode } from 'react';
import Brand from '@/components/common/Brand';
import ImageSlot from '@/components/common/ImageSlot';

/**
 * Split-screen shell shared by every login and signup page.
 * The image panel is decorative and hidden below `lg` — on a phone the form
 * gets the whole screen, which is where most of this market signs up.
 */
export default function AuthLayout({
  children,
  image,
  imageAlt,
  quote,
  attribution,
}: {
  children: ReactNode;
  image: string;
  imageAlt: string;
  quote: string;
  attribution: string;
}) {
  return (
    <div className="grid min-h-dvh lg:grid-cols-[1fr_1fr]">
      {/* ── Form side ──────────────────────────────────────────────────── */}
      <div className="flex flex-col px-5 py-7 sm:px-10">
        <Brand />

        <div className="flex flex-1 items-center py-10">
          <div className="animate-rise mx-auto w-full max-w-sm">{children}</div>
        </div>
      </div>

      {/* ── Image side ─────────────────────────────────────────────────── */}
      <div className="relative hidden overflow-hidden lg:block">
        <ImageSlot
          name={image}
          alt={imageAlt}
          width={450}
          height={600}
          priority
          className="absolute inset-0 size-full"
        />

        {/* Scrim so the quote stays legible over any photograph */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to top, rgb(20 17 13 / 0.82) 0%, rgb(20 17 13 / 0.35) 45%, transparent 75%)',
          }}
        />

        <figure className="absolute right-10 bottom-10 left-10">
          <blockquote className="font-display text-2xl leading-snug text-white">
            “{quote}”
          </blockquote>
          <figcaption className="mt-3 text-sm text-white/70">{attribution}</figcaption>
        </figure>
      </div>
    </div>
  );
}
